import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Divider,
  Row,
  Col,
  Table,
  Tag,
  Alert,
  DatePicker,
  AutoComplete,
  message,
  Modal,
  List,
  Typography
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faFlask, faPlus, faTimes, faClipboardCheck, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { PRIORITY_LEVELS, TEST_PANELS } from '../utils/constants'
import { getTests } from '../utils/utils'
import { useLabOrders } from '../hooks/useLabOrders'
import moment from 'moment'
import { readPatients } from '../../Admin/patients/utils/utils'
import dayjs from 'dayjs'

const { Option } = Select
const { TextArea } = Input
const { Title, Text } = Typography

const NewOrder = ({ onComplete, onFormDataChange, initialFormData }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [patients, setPatients] = useState([])
  const [availableTests, setAvailableTests] = useState([])
  const [selectedTests, setSelectedTests] = useState([])
  const [searchingPatient, setSearchingPatient] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [showTestModal, setShowTestModal] = useState(false)
  const [testCategory, setTestCategory] = useState('all')
  const [categories, setCategories] = useState([])
  const [subCategories, setSubCategories] = useState([])
  const [testSubCategory, setTestSubCategory] = useState('all')
  const [testSearchText, setTestSearchText] = useState('')
  const [loadingTests, setLoadingTests] = useState(false)
  const [testPagination, setTestPagination] = useState({ current: 1, pageSize: 50, total: 0 })
  const [hasMoreTests, setHasMoreTests] = useState(true)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const searchTimeoutRef = useRef(null)
  const testSearchTimeoutRef = useRef(null)
  const persistTimeoutRef = useRef(null)
  const listContainerRef = useRef(null)

  const { createOrder } = useLabOrders()

  // Function to get category colors
  const getCategoryColor = (category) => {
    const colorMap = {
      Hematology: 'red',
      Biochemistry: 'blue',
      Microbiology: 'green',
      Immunology: 'purple',
      Pathology: 'orange',
      Radiology: 'cyan',
      Allergy: 'magenta',
      Cardiology: 'volcano',
      Endocrinology: 'geekblue',
      Oncology: 'gold',
      'Infectious Diseases': 'lime',
      LabAmpath: 'orange',
      'Molecular Biology': 'cyan',
      Pharmacology: 'purple',
      Serology: 'geekblue'
    }
    return colorMap[category] || 'default'
  }

  // Debounced search function
  const debouncedSearch = useCallback((searchQuery, page, pageLimit) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      handlePatientSearch(searchQuery, page, pageLimit)
    }, 1000)
  }, [])

  // Scroll detection for load more
  const handleScroll = useCallback(() => {
    if (listContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listContainerRef.current
      const threshold = 10 // 10px threshold to trigger "at bottom"
      const atBottom = scrollTop + clientHeight >= scrollHeight - threshold
      setIsAtBottom(atBottom)
    }
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      if (testSearchTimeoutRef.current) {
        clearTimeout(testSearchTimeoutRef.current)
      }
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    handlePatientSearch('', 1, 50)
    loadCategoriesAndSubCategories()
  }, [])

  // Restore persisted form data when component mounts
  useEffect(() => {
    if (initialFormData) {
      // Restore form fields
      form.setFieldsValue(initialFormData.formFields)

      // Restore selected patient
      if (initialFormData.selectedPatient) {
        setSelectedPatient(initialFormData.selectedPatient)
      }

      // Restore selected tests
      if (initialFormData.selectedTests) {
        setSelectedTests(initialFormData.selectedTests)
      }
    }
  }, [initialFormData])

  // Debounced persistence to prevent flickering
  const persistFormData = useCallback(() => {
    if (onFormDataChange) {
      const formFields = form.getFieldsValue()
      const formData = {
        formFields,
        selectedPatient,
        selectedTests
      }
      onFormDataChange(formData)
    }
  }, [onFormDataChange, form, selectedPatient, selectedTests])

  // Debounced version to reduce flicker
  const debouncedPersist = useCallback(() => {
    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current)
    }
    persistTimeoutRef.current = setTimeout(persistFormData, 100)
  }, [persistFormData])

  // Persist form data when it changes (debounced)
  useEffect(() => {
    debouncedPersist()
  }, [selectedPatient, selectedTests, debouncedPersist])

  // Also persist when form fields change (debounced)
  const handleFormChange = useCallback(() => {
    debouncedPersist()
  }, [debouncedPersist])

  // Load categories and subcategories for filters
  const loadCategoriesAndSubCategories = async () => {
    try {
      // Get a sample of tests to extract categories and subcategories
      const sampleTests = await getTests({}, 1, 1000, 'category subCategory')

      const uniqueCategories = [...new Set(sampleTests.map((test) => test.category))].filter(Boolean)
      const uniqueSubCategories = [...new Set(sampleTests.map((test) => test.subCategory))].filter(Boolean)

      setCategories(uniqueCategories.sort())
      setSubCategories(uniqueSubCategories.sort())
    } catch (e) {
      console.error('Error loading categories:', e)
    }
  }

  // Debounced test search function
  const debouncedTestSearch = useCallback((searchText, category, subCategory, page = 1) => {
    if (testSearchTimeoutRef.current) {
      clearTimeout(testSearchTimeoutRef.current)
    }

    testSearchTimeoutRef.current = setTimeout(() => {
      loadTests(searchText, category, subCategory, page)
    }, 500)
  }, [])

  // Load tests with filters and search
  const loadTests = async (searchText = '', category = 'all', subCategory = 'all', page = 1, append = false) => {
    try {
      setLoadingTests(true)

      // Build filter object
      let filter = {}

      if (category !== 'all') {
        filter.category = category
      }

      if (subCategory !== 'all') {
        filter.subCategory = subCategory
      }

      if (searchText && searchText.trim()) {
        filter.$or = [
          { name: { $regex: searchText.trim(), $options: 'i' } },
          { code: { $regex: searchText.trim(), $options: 'i' } }
        ]
      }

      const tests = await getTests(filter, page, testPagination.pageSize)

      // Check if we've reached the end of results
      const reachedEnd = tests.length < testPagination.pageSize
      setHasMoreTests(!reachedEnd)

      if (append) {
        setAvailableTests((prev) => [...prev, ...tests])
        setTestPagination((prev) => ({ ...prev, current: page }))
      } else {
        setAvailableTests(tests)
        setTestPagination((prev) => ({ ...prev, current: page }))
      }
    } catch (e) {
      message.error('Failed to load tests')
      console.error('Error loading tests:', e)
    } finally {
      setLoadingTests(false)
    }
  }

  const handlePatientSearch = async (searchQuery, page, pageLimit) => {
    try {
      let qry = {}

      setSearchingPatient(true)

      if (searchQuery) {
        qry = {
          $or: [
            {
              $expr: {
                $regexMatch: {
                  input: { $concat: ['$firstName', ' ', '$lastName'] },
                  regex: searchQuery,
                  options: 'i'
                }
              }
            },
            { phoneNumber: { $regex: searchQuery, $options: 'i' } },
            { email: { $regex: searchQuery, $options: 'i' } },
            { idNo: { $regex: searchQuery, $options: 'i' } }
          ]
        }
      }

      const patients = await readPatients(qry, {}, '-profileImage', page, pageLimit)

      setPatients(patients)
    } catch (e) {
      console.log('Error', e)
      message.error('Failed to search for patients')
    } finally {
      setSearchingPatient(false)
    }
  }

  const handlePatientSelect = (value, option) => {
    const patient = patients.find((p) => p._id === option.key)
    setSelectedPatient(patient)
    form.setFieldsValue({
      patientSearch: '', // Clear the search field
      patientName: `${patient.firstName} ${patient.lastName}`,
      patientDOB: moment(patient.dateOfBirth),
      gender: patient.gender
    })
  }

  const handleAddTest = useCallback((test) => {
    setSelectedTests((prevTests) => {
      const exists = prevTests.find((t) => t._id === test._id)
      return exists ? prevTests : [...prevTests, test]
    })
  }, [])

  const handleRemoveTest = useCallback((testId) => {
    setSelectedTests((prevTests) => prevTests.filter((t) => t._id !== testId))
  }, [])

  const handleTestCategoryChange = (category) => {
    setTestCategory(category)
    setHasMoreTests(true) // Reset when filter changes
    setIsAtBottom(false) // Reset scroll state
    debouncedTestSearch(testSearchText, category, testSubCategory, 1)
  }

  const handleTestSubCategoryChange = (subCategory) => {
    setTestSubCategory(subCategory)
    setHasMoreTests(true) // Reset when filter changes
    setIsAtBottom(false) // Reset scroll state
    debouncedTestSearch(testSearchText, testCategory, subCategory, 1)
  }

  const handleTestSearchChange = (searchText) => {
    setTestSearchText(searchText)
    setHasMoreTests(true) // Reset when search changes
    setIsAtBottom(false) // Reset scroll state
    debouncedTestSearch(searchText, testCategory, testSubCategory, 1)
  }

  const handleLoadMoreTests = () => {
    setIsAtBottom(false) // Hide button after clicking
    loadTests(testSearchText, testCategory, testSubCategory, testPagination.current + 1, true)
  }

  const handleOpenTestModal = () => {
    setShowTestModal(true)
    setHasMoreTests(true) // Reset when modal opens
    setIsAtBottom(false) // Reset scroll state
    // Load initial tests when modal opens
    loadTests(testSearchText, testCategory, testSubCategory, 1)
  }

  const handleAddTestPanel = (panelKey) => {
    // TODO: We need to generate the panels from the ampath test set
    const panel = TEST_PANELS[panelKey]
    const testsToAdd = availableTests.filter((test) => panel.tests.some((panelTest) => test.name.includes(panelTest)))

    testsToAdd.forEach((test) => handleAddTest(test))
    message.success(`Added ${panel.name} tests`)
  }

  const handleSubmit = async (values) => {
    if (selectedTests.length === 0) {
      message.error('Please select at least one test')
      return
    }

    setLoading(true)
    try {
      const orderData = {
        patient: selectedPatient,
        orderedTests: selectedTests.map((t) => t.code),
        priority: values.priority === PRIORITY_LEVELS.STAT ? 'S' : 'R',
        collectionDate: dayjs(values.collectionDate).format('YYYY-MM-DD'),
        collectionTime: dayjs(values.collectionDate).format('HH:mm'),
        notes: values.clinicalNotes
      }

      await createOrder(orderData)
      form.resetFields()
      setSelectedTests([])
      setSelectedPatient(null)
      onComplete()
    } catch (error) {
      message.error('Failed to create lab order')
    } finally {
      setLoading(false)
    }
  }

  const testColumns = [
    {
      title: 'Test Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Sub Category',
      dataIndex: 'subCategory',
      key: 'subCategory'
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code'
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          size='small'
          type='link'
          danger
          icon={<FontAwesomeIcon icon={faTimes} />}
          onClick={() => handleRemoveTest(record._id)}
        >
          Remove
        </Button>
      )
    }
  ]

  return (
    <Card style={{ height: '100%', overflow: 'auto' }}>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
        initialValues={{
          priority: PRIORITY_LEVELS.STAT
        }}
      >
        <Row gutter={24}>
          <Col span={24}>
            <Title level={4} style={{ textAlign: 'center', margin: '0 0 24px 0' }}>
              Create New Lab Order
            </Title>
            <Divider />
          </Col>
        </Row>

        {/* Patient Selection */}
        <Row gutter={16}>
          <Col span={24}>
            <Card title='Patient Information' type='inner' style={{ marginBottom: 32 }}>
              {!selectedPatient ? (
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name='patientSearch'
                      label='Search Patient'
                      rules={[{ required: true, message: 'Please select a patient' }]}
                    >
                      <AutoComplete
                        placeholder='Search by name or ID'
                        onSearch={(value) => debouncedSearch(value, 1, 50)}
                        onSelect={handlePatientSelect}
                        loading={searchingPatient}
                        size='large'
                        style={{ width: '100%' }}
                        options={patients.map((p) => ({
                          value: `${p.firstName} ${p.lastName}`,
                          key: p._id,
                          label: (
                            <div>
                              <div>
                                {p.firstName} {p.lastName}
                              </div>
                              <small style={{ color: '#8c8c8c' }}>{p.idNo || 'No ID'}</small>
                            </div>
                          )
                        }))}
                        suffixIcon={
                          searchingPatient ? <FontAwesomeIcon icon={faSpinner} /> : <FontAwesomeIcon icon={faSearch} />
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <div
                  style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: '6px',
                    padding: '16px',
                    position: 'relative'
                  }}
                >
                  <Button
                    type='text'
                    size='small'
                    icon={<FontAwesomeIcon icon={faTimes} />}
                    onClick={() => {
                      setSelectedPatient(null)
                      form.setFieldsValue({
                        patientSearch: '',
                        patientName: '',
                        patientDOB: null,
                        gender: undefined
                      })
                    }}
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      color: '#52c41a',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px',
                      minWidth: '24px',
                      zIndex: 1
                    }}
                  />
                  <Row gutter={16}>
                    <Col span={8}>
                      <div>
                        <Text strong style={{ color: '#52c41a' }}>
                          Patient Name
                        </Text>
                        <div style={{ fontSize: '16px', marginTop: '4px' }}>
                          {selectedPatient.firstName} {selectedPatient.lastName}
                        </div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <Text strong style={{ color: '#52c41a' }}>
                          Date of Birth
                        </Text>
                        <div style={{ fontSize: '16px', marginTop: '4px' }}>
                          {moment(selectedPatient.dateOfBirth).format('DD-MM-YYYY')}
                        </div>
                      </div>
                    </Col>
                    <Col span={4}>
                      <div>
                        <Text strong style={{ color: '#52c41a' }}>
                          Gender
                        </Text>
                        <div style={{ fontSize: '16px', marginTop: '4px', textTransform: 'capitalize' }}>
                          {selectedPatient.gender}
                        </div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <Text strong style={{ color: '#52c41a' }}>
                          ID Number
                        </Text>
                        <div style={{ fontSize: '16px', marginTop: '4px' }}>{selectedPatient.idNo || 'No ID'}</div>
                      </div>
                    </Col>
                  </Row>
                  {/* Hidden form fields to maintain form validation */}
                  <div style={{ display: 'none' }}>
                    <Form.Item name='patientName'>
                      <Input />
                    </Form.Item>
                    <Form.Item name='patientDOB'>
                      <DatePicker />
                    </Form.Item>
                    <Form.Item name='gender'>
                      <Select />
                    </Form.Item>
                  </div>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Test Selection */}
        <Row gutter={16}>
          <Col span={24}>
            <Card
              title='Test Selection'
              type='inner'
              style={{ marginBottom: 32 }}
              extra={
                <Space>
                  <Button type='primary' icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleOpenTestModal}>
                    Add Tests
                  </Button>
                  <Select
                    placeholder='Quick Add Panel'
                    style={{ width: 200 }}
                    onChange={handleAddTestPanel}
                    value={null}
                  >
                    {Object.entries(TEST_PANELS).map(([key, panel]) => (
                      <Option key={key} value={key}>
                        {panel.name}
                      </Option>
                    ))}
                  </Select>
                </Space>
              }
            >
              {selectedTests.length > 0 ? (
                <Table columns={testColumns} dataSource={selectedTests} rowKey='id' pagination={false} size='small' />
              ) : (
                <Alert
                  message='No tests selected'
                  description="Click 'Add Tests' to select laboratory tests for this order"
                  type='info'
                  showIcon
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Order Details */}
        <Row gutter={16}>
          <Col span={24}>
            <Card title='Order Details' type='inner' style={{ marginBottom: 32 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name='priority' label='Priority' rules={[{ required: true }]}>
                    <Select>
                      {Object.entries(PRIORITY_LEVELS).map(([key, value]) => (
                        <Option key={key} value={value}>
                          <Tag
                            color={
                              value === PRIORITY_LEVELS.STAT
                                ? 'red'
                                : value === PRIORITY_LEVELS.ROUTINE
                                ? 'blue'
                                : 'green'
                            }
                          >
                            {value}
                          </Tag>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name='collectionDate' label='Collection Date/Time' rules={[{ required: true }]}>
                    <DatePicker
                      showTime={{ format: 'HH:mm' }}
                      format='YYYY-MM-DD HH:mm'
                      style={{ width: '100%' }}
                      // disable any dates and times before now
                      disabledDate={(current) => current && current < moment().startOf('day')}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name='clinicalNotes' label='Clinical Notes / Reason for Test'>
                    <TextArea rows={3} placeholder='Enter clinical notes or reason for ordering these tests' />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Form Actions */}
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={onComplete}>Cancel</Button>
              <Button
                type='primary'
                htmlType='submit'
                loading={loading}
                icon={<FontAwesomeIcon icon={faClipboardCheck} />}
              >
                Create Order
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>

      {/* Test Selection Modal */}
      <Modal
        title='Select Laboratory Tests'
        open={showTestModal}
        onCancel={() => {
          setShowTestModal(false)
          setTestSearchText('')
          setTestCategory('all')
          setTestSubCategory('all')
          setAvailableTests([])
          setHasMoreTests(true)
          setIsAtBottom(false)
        }}
        width={1000}
        footer={[
          <Button
            key='close'
            onClick={() => {
              setShowTestModal(false)
              setTestSearchText('')
              setTestCategory('all')
              setTestSubCategory('all')
              setAvailableTests([])
              setHasMoreTests(true)
              setIsAtBottom(false)
            }}
            style={{
              color: '#ff4d4f',
              borderColor: '#ff4d4f'
            }}
          >
            Close
          </Button>
        ]}
      >
        <Space direction='vertical' style={{ width: '100%' }} size='middle'>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Input
                placeholder='Search tests...'
                value={testSearchText}
                onChange={(e) => handleTestSearchChange(e.target.value)}
                prefix={<FontAwesomeIcon icon={faSearch} />}
                allowClear
              />
            </Col>
            <Col span={8}>
              <Select value={testCategory} onChange={handleTestCategoryChange} style={{ width: '100%' }}>
                <Option value='all'>All Categories</Option>
                {categories.map((cat) => (
                  <Option key={cat} value={cat}>
                    <Tag color={getCategoryColor(cat)}>{cat}</Tag>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <Select value={testSubCategory} onChange={handleTestSubCategoryChange} style={{ width: '100%' }}>
                <Option value='all'>All Sub Categories</Option>
                {subCategories.map((cat) => (
                  <Option key={cat} value={cat}>
                    {cat}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          <div>
            <div ref={listContainerRef} onScroll={handleScroll} style={{ maxHeight: 400, overflow: 'auto' }}>
              <List
                dataSource={availableTests}
                loading={loadingTests}
                renderItem={(test) => {
                  const isSelected = selectedTests.some((t) => t._id === test._id)
                  return (
                    <List.Item
                      actions={[
                        <Button
                          key={`${test._id}-btn`}
                          type='link'
                          onClick={() => {
                            if (isSelected) {
                              handleRemoveTest(test._id)
                            } else {
                              handleAddTest(test)
                            }
                          }}
                          style={{
                            color: isSelected ? '#ff4d4f' : '#1890ff'
                          }}
                        >
                          {isSelected ? 'Remove' : 'Add'}
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        title={test.name}
                        description={
                          <Space direction='vertical' size={0}>
                            <Text style={{ color: '#595959', fontSize: '13px' }}>
                              Category: <Tag color={getCategoryColor(test.category)}>{test.category}</Tag>
                            </Text>
                            <Text style={{ color: '#595959', fontSize: '13px' }}>Sub Category: {test.subCategory}</Text>
                            <Text style={{ color: '#595959', fontSize: '13px' }}>Code: {test.code}</Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )
                }}
              />
            </div>

            {availableTests.length > 0 && hasMoreTests && isAtBottom && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button onClick={handleLoadMoreTests} loading={loadingTests}>
                  Load More
                </Button>
              </div>
            )}
          </div>
        </Space>
      </Modal>
    </Card>
  )
}

export default NewOrder
